import React, { useEffect, useMemo, useState } from 'react';
import { EditIcon, TrashIcon } from '@iconicicons/react';
import toast from 'react-hot-toast';
import Select, { SingleValue } from 'react-select';
import { ReusableModal } from '../modals/ReusableModal';
import { ReduxPointSearch } from '../search/ReduxPointSearch';
import { useLazyGetKatanaContractUsagesQuery } from '../services/edgeApi';
import { Contract } from '../types';
import { KatanaContractUsageSummary } from '../types/api/KatanaContractUsage';
import {
  ContractUsageDraft,
  ContractUsageWarning,
  contractUsageError,
  findInvalidEnabledContractUsageIndex,
  getContractUsageWarningsByRowId,
  getEnabledContractUsages,
} from './contractUsageDraft';

export type ContractPickOption = {
  value: number;
  label: string;
  contract: Contract;
};

type ContractUsageManagerModalProps = {
  isOpen: boolean;
  usages: ContractUsageDraft[];
  warnings: ContractUsageWarning[];
  contractOptions: ContractPickOption[];
  contractsLoading: boolean;
  defaultNominationGasDate?: string;
  pointLabel: (pointId: number | null) => string;
  createUsage: () => ContractUsageDraft;
  onApply: (usages: ContractUsageDraft[]) => void;
  onClose: () => void;
};

export const ContractUsageManagerModal: React.FC<
  ContractUsageManagerModalProps
> = ({
  isOpen,
  usages,
  warnings,
  contractOptions,
  contractsLoading,
  defaultNominationGasDate,
  pointLabel,
  createUsage,
  onApply,
  onClose,
}) => {
  const [draftUsages, setDraftUsages] = useState<ContractUsageDraft[]>([]);
  const [showValidation, setShowValidation] = useState(false);
  const [expandedRowId, setExpandedRowId] = useState<number | null>(null);
  const [nominationGasDate, setNominationGasDate] = useState(
    defaultNominationGasDate ?? '',
  );
  const [katanaImportResult, setKatanaImportResult] = useState<{
    gasDate: string;
    diagnosticsUrl: string;
    summary: KatanaContractUsageSummary;
  } | null>(null);
  const [loadKatanaUsages, { isFetching: isKatanaLoading }] =
    useLazyGetKatanaContractUsagesQuery();

  const contractOptionById = useMemo(
    () => new Map(contractOptions.map((option) => [option.value, option])),
    [contractOptions],
  );

  const scopedDraftUsages = useMemo(
    () =>
      draftUsages.map((usage) => {
        const contract =
          usage.contractId == null
            ? undefined
            : contractOptionById.get(usage.contractId)?.contract;
        return contract?.is_segmentable === false &&
          usage.capacityScope !== 'contract'
          ? { ...usage, capacityScope: 'contract' as const }
          : usage;
      }),
    [draftUsages, contractOptionById],
  );

  const warningByRowId = useMemo(() => {
    return getContractUsageWarningsByRowId(usages, warnings);
  }, [usages, warnings]);

  useEffect(() => {
    setNominationGasDate(defaultNominationGasDate ?? '');
  }, [defaultNominationGasDate]);

  useEffect(() => {
    if (!isOpen) return;
    setDraftUsages(usages.map((usage) => ({ ...usage })));
    setShowValidation(false);
    setKatanaImportResult(null);
    const firstWarningUsage = usages.find((usage) =>
      warningByRowId.has(usage.rowId),
    );
    setExpandedRowId(firstWarningUsage?.rowId ?? null);
  }, [isOpen, usages, warningByRowId]);

  const updateUsage = (rowId: number, patch: Partial<ContractUsageDraft>) => {
    setDraftUsages((current) =>
      current.map((usage) =>
        usage.rowId === rowId ? { ...usage, ...patch } : usage,
      ),
    );
  };

  const selectContract = (
    rowId: number,
    selected: SingleValue<ContractPickOption>,
  ) => {
    if (!selected) {
      updateUsage(rowId, { contractId: null });
      return;
    }

    // The draft keeps the chosen scope; scopedDraftUsages forces whole-contract scope
    // for non-segmentable contracts.
    const patch: Partial<ContractUsageDraft> = { contractId: selected.value };
    if (selected.contract.receipt_points?.length === 1) {
      patch.receiptPointId = Number(selected.contract.receipt_points[0]);
    }
    if (selected.contract.delivery_points?.length === 1) {
      patch.deliveryPointId = Number(selected.contract.delivery_points[0]);
    }
    updateUsage(rowId, patch);
  };

  const apply = () => {
    const invalidUsageIndex =
      findInvalidEnabledContractUsageIndex(scopedDraftUsages);
    if (invalidUsageIndex >= 0) {
      setShowValidation(true);
      const invalidUsage = scopedDraftUsages[invalidUsageIndex];
      setExpandedRowId(invalidUsage.rowId);
      toast.error(
        `Contract usage ${invalidUsageIndex + 1}: ${contractUsageError(
          invalidUsage,
        )}.`,
      );
      return;
    }

    onApply(scopedDraftUsages.map((usage) => ({ ...usage })));
  };

  const enabledCount = getEnabledContractUsages(draftUsages).length;

  const addUsage = () => {
    const usage = createUsage();
    setDraftUsages((current) => [...current, usage]);
    setExpandedRowId(usage.rowId);
    setShowValidation(false);
  };

  const hasKatanaUsages = draftUsages.some(
    (usage) => usage.source === 'katana',
  );

  const pullKatanaUsages = async () => {
    if (!nominationGasDate || isKatanaLoading) return;

    try {
      const response = await loadKatanaUsages({
        gas_date: nominationGasDate,
      }).unwrap();
      const importedUsages = response.usages.map(
        (usage): ContractUsageDraft => ({
          ...createUsage(),
          enabled: true,
          source: 'katana',
          sourceKey: usage.source_key,
          sourceRecordCount: usage.nomination_count,
          sourceContractName: usage.contract_name,
          sourceContractTspName: usage.contract_tsp_name,
          sourceReceiptPointName: usage.receipt_point_name,
          sourceDeliveryPointName: usage.delivery_point_name,
          contractId: usage.contract_id,
          capacityScope: usage.capacity_scope,
          receiptPointId: usage.receipt_point_id,
          deliveryPointId: usage.delivery_point_id,
          deliveryVolume: usage.delivery_volume,
        }),
      );

      setDraftUsages((current) => [
        ...current.filter((usage) => usage.source !== 'katana'),
        ...importedUsages,
      ]);
      setExpandedRowId(null);
      setShowValidation(false);
      setKatanaImportResult({
        gasDate: response.gas_date,
        diagnosticsUrl: response.diagnostics_url,
        summary: response.summary,
      });
    } catch (_error) {
      toast.error('Failed to load contract usages from Katana.');
    }
  };

  const removeUsage = (rowId: number) => {
    setDraftUsages((current) =>
      current.filter((usage) => usage.rowId !== rowId),
    );
    setExpandedRowId((current) => (current === rowId ? null : current));
  };

  const volumeLabel = (usage: ContractUsageDraft): string => {
    if (usage.deliveryVolume.trim() === '') return '—';
    const volume = Number(usage.deliveryVolume);
    return Number.isFinite(volume) ? volume.toLocaleString() : '—';
  };

  return (
    <ReusableModal
      header="Manage Contract Usages"
      isOpen={isOpen}
      onClose={onClose}
      size="xl"
      style={{
        maxWidth: 'calc(100vw - var(--bs-modal-margin) * 2)',
      }}
      scrollable
      footer={
        <>
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={onClose}
          >
            Cancel
          </button>
          <button type="button" className="btn btn-primary" onClick={apply}>
            Apply
          </button>
        </>
      }
    >
      <div className="d-flex flex-column gap-3">
        <div className="d-flex align-items-start justify-content-between gap-3">
          <div>
            <p className="mb-1">
              Record contract capacity already committed before this search.
            </p>
            <small className="text-muted">
              Only active usages are validated and included in Find Paths.
            </small>
          </div>
          <span className="badge bg-light text-dark text-nowrap">
            {enabledCount} of {draftUsages.length} active
          </span>
        </div>

        <div className="d-flex flex-wrap justify-content-between gap-2">
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            disabled={draftUsages.length === 0}
            onClick={() => {
              setDraftUsages([]);
              setExpandedRowId(null);
              setShowValidation(false);
              setKatanaImportResult(null);
            }}
          >
            Clear All
          </button>
          <div className="d-flex flex-wrap gap-2">
            <div className="input-group input-group-sm w-auto">
              <label
                className="input-group-text"
                htmlFor="contract-usage-nomination-gas-date"
              >
                Nomination Gas Date
              </label>
              <input
                id="contract-usage-nomination-gas-date"
                className="form-control"
                type="date"
                value={nominationGasDate}
                onChange={(event) => setNominationGasDate(event.target.value)}
              />
              <button
                type="button"
                className="btn btn-outline-secondary"
                disabled={!nominationGasDate || isKatanaLoading}
                onClick={pullKatanaUsages}
                title={
                  nominationGasDate
                    ? undefined
                    : 'Select a nomination gas date before loading Katana usages'
                }
              >
                {isKatanaLoading
                  ? 'Loading Katana…'
                  : hasKatanaUsages
                  ? 'Refresh Katana'
                  : 'Pull from Katana'}
              </button>
            </div>
            <button
              type="button"
              className="btn btn-sm btn-outline-primary"
              onClick={addUsage}
            >
              Add Usage
            </button>
          </div>
        </div>

        {katanaImportResult && (
          <div className="alert alert-info py-2 px-3 mb-0" role="status">
            <div className="small">
              <strong>Katana refresh for {katanaImportResult.gasDate}:</strong>{' '}
              {katanaImportResult.summary.usage_count}{' '}
              {katanaImportResult.summary.usage_count === 1
                ? 'usage'
                : 'usages'}{' '}
              from {katanaImportResult.summary.imported_nominations}{' '}
              {katanaImportResult.summary.imported_nominations === 1
                ? 'nomination'
                : 'nominations'}
              . {katanaImportResult.summary.skipped_nominations} skipped.{' '}
              {katanaImportResult.summary.imported_nominations_with_issues}{' '}
              imported with issues.
              {(katanaImportResult.summary.skipped_nominations > 0 ||
                katanaImportResult.summary.imported_nominations_with_issues >
                  0) && (
                <>
                  {' '}
                  <a
                    href={katanaImportResult.diagnosticsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Review diagnostics
                  </a>
                </>
              )}
            </div>
            {katanaImportResult.summary.skipped_by_reason.length > 0 && (
              <ul className="small mb-0 mt-1 ps-3">
                {katanaImportResult.summary.skipped_by_reason.map(
                  (diagnostic) => (
                    <li key={diagnostic.code}>
                      Skipped - {diagnostic.count}: {diagnostic.message}
                    </li>
                  ),
                )}
              </ul>
            )}
            {katanaImportResult.summary.issues_by_reason.length > 0 && (
              <ul className="small mb-0 mt-1 ps-3">
                {katanaImportResult.summary.issues_by_reason.map(
                  (diagnostic) => (
                    <li key={diagnostic.code}>
                      Imported with issues - {diagnostic.count}:{' '}
                      {diagnostic.message}
                    </li>
                  ),
                )}
              </ul>
            )}
          </div>
        )}

        {draftUsages.length === 0 ? (
          <div className="border rounded p-3 text-muted small">
            No contract usages have been defined.
          </div>
        ) : (
          <div className="table-responsive border rounded">
            <table className="table table-sm table-striped table-hover align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th scope="col" className="text-center">
                    Active
                  </th>
                  <th scope="col">Source</th>
                  <th scope="col">Contract</th>
                  <th scope="col">Capacity Scope</th>
                  <th scope="col">Receipt Point</th>
                  <th scope="col">Delivery Point</th>
                  <th scope="col" className="text-end text-nowrap">
                    Existing Volume
                  </th>
                  <th scope="col">Status</th>
                  <th scope="col" className="text-end">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {scopedDraftUsages.map((usage, index) => {
                  const matchingContract =
                    (usage.contractId != null &&
                      contractOptionById.get(usage.contractId)) ||
                    null;
                  const selectedContract =
                    matchingContract ||
                    (usage.contractId != null && usage.sourceContractName
                      ? {
                          value: usage.contractId,
                          label: `${usage.sourceContractName} (#${usage.contractId})`,
                          contract: {
                            id: usage.contractId,
                            name: usage.sourceContractName,
                            tsp_name: usage.sourceContractTspName,
                          } as Contract,
                        }
                      : null);
                  const usageError = usage.enabled
                    ? contractUsageError(usage)
                    : null;
                  const validationError = showValidation ? usageError : null;
                  const rowWarnings = warningByRowId.get(usage.rowId) || [];
                  const isExpanded = expandedRowId === usage.rowId;
                  const contractLabel = selectedContract
                    ? `${selectedContract.contract.name} (#${selectedContract.value})`
                    : usage.sourceContractName && usage.contractId != null
                    ? `${usage.sourceContractName} (#${usage.contractId})`
                    : usage.contractId != null
                    ? `Contract #${usage.contractId}`
                    : '—';
                  const receiptLabel =
                    usage.capacityScope === 'contract' ||
                    usage.receiptPointId == null
                      ? '—'
                      : usage.sourceReceiptPointName
                      ? `${usage.sourceReceiptPointName} #${usage.receiptPointId}`
                      : pointLabel(usage.receiptPointId);
                  const deliveryLabel =
                    usage.capacityScope === 'contract' ||
                    usage.deliveryPointId == null
                      ? '—'
                      : usage.sourceDeliveryPointName
                      ? `${usage.sourceDeliveryPointName} #${usage.deliveryPointId}`
                      : pointLabel(usage.deliveryPointId);

                  return (
                    <React.Fragment key={usage.rowId}>
                      <tr className={usage.enabled ? undefined : 'text-muted'}>
                        <td className="text-center">
                          <div className="form-check form-switch d-inline-flex mb-0">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              role="switch"
                              aria-label={`Toggle usage ${index + 1}`}
                              checked={usage.enabled}
                              onChange={(event) =>
                                updateUsage(usage.rowId, {
                                  enabled: event.target.checked,
                                })
                              }
                            />
                          </div>
                        </td>
                        <td>
                          {usage.source === 'katana' ? (
                            <div className="d-flex flex-column align-items-start gap-1">
                              <span className="badge text-bg-info">Katana</span>
                              {usage.sourceRecordCount != null && (
                                <small className="text-muted text-nowrap">
                                  {usage.sourceRecordCount}{' '}
                                  {usage.sourceRecordCount === 1
                                    ? 'nomination'
                                    : 'nominations'}
                                </small>
                              )}
                            </div>
                          ) : (
                            <span className="badge bg-light text-dark">
                              Manual
                            </span>
                          )}
                        </td>
                        <td>
                          <div
                            className="text-truncate"
                            style={{ minWidth: 160, maxWidth: 260 }}
                            title={contractLabel}
                          >
                            {contractLabel}
                          </div>
                          {(selectedContract?.contract.tsp_name ||
                            usage.sourceContractTspName) && (
                            <small className="text-muted">
                              {selectedContract?.contract.tsp_name ||
                                usage.sourceContractTspName}
                            </small>
                          )}
                        </td>
                        <td className="text-nowrap">
                          {usage.capacityScope === 'contract'
                            ? 'Whole contract'
                            : 'Route between endpoints'}
                        </td>
                        <td>
                          <div
                            className="text-truncate"
                            style={{ minWidth: 130, maxWidth: 220 }}
                            title={receiptLabel}
                          >
                            {receiptLabel}
                          </div>
                        </td>
                        <td>
                          <div
                            className="text-truncate"
                            style={{ minWidth: 130, maxWidth: 220 }}
                            title={deliveryLabel}
                          >
                            {deliveryLabel}
                          </div>
                        </td>
                        <td className="text-end text-nowrap">
                          {volumeLabel(usage)}
                        </td>
                        <td>
                          {rowWarnings.length > 0 ? (
                            <span
                              className="badge text-bg-warning"
                              title={rowWarnings
                                .map((warning) => warning.message)
                                .join('\n')}
                            >
                              Warning
                            </span>
                          ) : !usage.enabled ? (
                            <span className="badge text-bg-secondary">
                              Inactive
                            </span>
                          ) : usageError ? (
                            <span className="badge text-bg-danger">
                              Incomplete
                            </span>
                          ) : (
                            <span className="badge text-bg-success">Ready</span>
                          )}
                        </td>
                        <td className="text-end text-nowrap">
                          <button
                            type="button"
                            className="btn btn-sm btn-link text-primary p-1 me-1"
                            aria-label={`${
                              isExpanded ? 'Collapse' : 'Edit'
                            } usage ${index + 1}`}
                            title={
                              isExpanded ? 'Collapse editor' : 'Edit usage'
                            }
                            onClick={() =>
                              setExpandedRowId(isExpanded ? null : usage.rowId)
                            }
                          >
                            <EditIcon width={18} height={18} />
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-link text-danger p-1"
                            aria-label={`Remove usage ${index + 1}`}
                            title="Remove usage"
                            onClick={() => removeUsage(usage.rowId)}
                          >
                            <TrashIcon width={18} height={18} />
                          </button>
                        </td>
                      </tr>

                      {isExpanded && (
                        <tr>
                          <td colSpan={9} className="bg-light p-3">
                            <div className="d-flex flex-column gap-3">
                              <div>
                                <label
                                  className="form-label small mb-1"
                                  htmlFor={`contract-usage-contract-${usage.rowId}`}
                                >
                                  Contract
                                </label>
                                <Select
                                  inputId={`contract-usage-contract-${usage.rowId}`}
                                  classNamePrefix="contract-usage-contract"
                                  value={selectedContract}
                                  options={contractOptions}
                                  onChange={(
                                    selected: SingleValue<ContractPickOption>,
                                  ) => selectContract(usage.rowId, selected)}
                                  isClearable
                                  isSearchable
                                  isLoading={contractsLoading}
                                  menuPosition="fixed"
                                  placeholder="Search contracts..."
                                  noOptionsMessage={() =>
                                    contractsLoading
                                      ? 'Loading contracts...'
                                      : 'No active contracts found'
                                  }
                                />
                              </div>

                              <div>
                                <label
                                  className="form-label small mb-1"
                                  htmlFor={`contract-usage-scope-${usage.rowId}`}
                                >
                                  Capacity Scope
                                </label>
                                <select
                                  id={`contract-usage-scope-${usage.rowId}`}
                                  className="form-select form-select-sm"
                                  value={usage.capacityScope}
                                  disabled={
                                    usage.contractId == null ||
                                    selectedContract?.contract
                                      .is_segmentable === false
                                  }
                                  onChange={(event) =>
                                    updateUsage(usage.rowId, {
                                      capacityScope:
                                        event.target.value === 'contract'
                                          ? 'contract'
                                          : 'route',
                                    })
                                  }
                                >
                                  <option value="route">
                                    Route between endpoints
                                  </option>
                                  <option value="contract">
                                    Whole contract
                                  </option>
                                </select>
                                <small className="text-muted">
                                  {selectedContract?.contract.is_segmentable ===
                                  false
                                    ? 'Non-segmentable contracts always apply usage to the whole contract.'
                                    : usage.capacityScope === 'contract'
                                    ? 'Reduces capacity on every route using this contract. Endpoints are not required.'
                                    : 'Reduces capacity on overlapping physical edges. Non-segmentable contracts apply usage to the whole contract.'}
                                </small>
                              </div>

                              <div className="row g-3">
                                {usage.capacityScope === 'route' && (
                                  <>
                                    <div className="col-12 col-lg-4">
                                      <label className="form-label small mb-1">
                                        Receipt Point
                                      </label>
                                      <div className="d-flex gap-1">
                                        <ReduxPointSearch
                                          value={
                                            receiptLabel === '—'
                                              ? ''
                                              : receiptLabel
                                          }
                                          placeholder="Select receipt point..."
                                          className="form-control form-control-sm"
                                          handler={({ id }) => {
                                            const pointId = Number(id);
                                            if (
                                              Number.isInteger(pointId) &&
                                              pointId > 0
                                            ) {
                                              updateUsage(usage.rowId, {
                                                receiptPointId: pointId,
                                              });
                                            }
                                          }}
                                        />
                                        <button
                                          type="button"
                                          className="btn btn-sm btn-outline-secondary"
                                          disabled={
                                            usage.receiptPointId == null
                                          }
                                          onClick={() =>
                                            updateUsage(usage.rowId, {
                                              receiptPointId: null,
                                            })
                                          }
                                        >
                                          Clear
                                        </button>
                                      </div>
                                    </div>

                                    <div className="col-12 col-lg-4">
                                      <label className="form-label small mb-1">
                                        Delivery Point
                                      </label>
                                      <div className="d-flex gap-1">
                                        <ReduxPointSearch
                                          value={
                                            deliveryLabel === '—'
                                              ? ''
                                              : deliveryLabel
                                          }
                                          placeholder="Select delivery point..."
                                          className="form-control form-control-sm"
                                          handler={({ id }) => {
                                            const pointId = Number(id);
                                            if (
                                              Number.isInteger(pointId) &&
                                              pointId > 0
                                            ) {
                                              updateUsage(usage.rowId, {
                                                deliveryPointId: pointId,
                                              });
                                            }
                                          }}
                                        />
                                        <button
                                          type="button"
                                          className="btn btn-sm btn-outline-secondary"
                                          disabled={
                                            usage.deliveryPointId == null
                                          }
                                          onClick={() =>
                                            updateUsage(usage.rowId, {
                                              deliveryPointId: null,
                                            })
                                          }
                                        >
                                          Clear
                                        </button>
                                      </div>
                                    </div>
                                  </>
                                )}
                                <div className="col-12 col-lg-4">
                                  <label
                                    className="form-label small mb-1"
                                    htmlFor={`contract-usage-volume-${usage.rowId}`}
                                  >
                                    Existing Delivery Volume (Dth)
                                  </label>
                                  <input
                                    id={`contract-usage-volume-${usage.rowId}`}
                                    type="number"
                                    className="form-control form-control-sm"
                                    min={0}
                                    step="any"
                                    value={usage.deliveryVolume}
                                    onChange={(event) =>
                                      updateUsage(usage.rowId, {
                                        deliveryVolume: event.target.value,
                                      })
                                    }
                                    placeholder="Volume already committed"
                                  />
                                </div>
                              </div>

                              {validationError && (
                                <div className="alert alert-danger py-2 px-3 mb-0 small">
                                  This active usage is incomplete:{' '}
                                  {validationError}.
                                </div>
                              )}

                              {rowWarnings.length > 0 && (
                                <div className="alert alert-warning py-2 px-3 mb-0">
                                  <ul className="small mb-0 ps-3">
                                    {rowWarnings.map((warning) => (
                                      <li
                                        key={`${warning.code}:${
                                          warning.field ?? ''
                                        }:${warning.message}`}
                                      >
                                        {warning.message}
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </ReusableModal>
  );
};
